/**
 * Personnel detail page — admin + owner only.
 * Shows profile info, current salary, salary history, and lets you add salary entries.
 */

import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '../components/Icon'
import { useAuth, authHeaders } from '../lib/auth'
import { useTranslation } from 'react-i18next'

// ── types ─────────────────────────────────────────────────────────────────────

interface Person {
  person_id:       number
  full_name:       string
  email:           string | null
  title:           string | null
  employment_type: string
  status:          string
  start_date:      string | null
  end_date:        string | null
  notes:           string | null
  created_at:      string
}

interface SalaryEntry {
  salary_id:          number
  person_id:          number
  effective_from:     string
  effective_to:       string | null
  amount_usd_minor:   number
  frequency:          string
  notes:              string | null
  created_at:         string
}

// ── helpers ───────────────────────────────────────────────────────────────────

const FREQ_LABEL: Record<string, string> = {
  monthly: '/mo', biweekly: '/2wk', weekly: '/wk', annual: '/yr', hourly: '/hr',
}

function fmtMoney(minor: number): string {
  return (minor / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

const STATUS_BADGE: Record<string, string> = {
  active:   'pill pill-mint',
  inactive: 'pill bg-ink-100 text-ink-500',
  on_leave: 'pill pill-honey',
}

// ── Add Salary Modal ──────────────────────────────────────────────────────────

function AddSalaryModal({ personId, onClose }: { personId: number; onClose: () => void }) {
  const { t } = useTranslation()
  const { token } = useAuth()
  const qc = useQueryClient()

  const [form, setForm] = useState({
    effective_from: '',
    effective_to:   '',
    amount_dollars: '',   // user enters dollars; we convert to minor
    frequency: 'monthly',
    notes: '',
  })
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: async () => {
      const amount_usd_minor = Math.round(parseFloat(form.amount_dollars) * 100)
      if (isNaN(amount_usd_minor) || amount_usd_minor <= 0) throw new Error('Enter a valid amount')

      const res = await fetch(`/api/personnel/${personId}/salary`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify({
          effective_from:   form.effective_from,
          effective_to:     form.effective_to || null,
          amount_usd_minor,
          frequency:        form.frequency,
          notes:            form.notes || null,
        }),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['personnel-salary', personId] })
      qc.invalidateQueries({ queryKey: ['personnel'] })
      onClose()
    },
    onError: (e: Error) => setError(e.message),
  })

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }))

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <h2 className="text-[15px] font-semibold">Add salary entry</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><Icon name="x" /></button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Amount (USD) *</label>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-ink-400 text-sm">$</span>
                <input
                  className="form-input pl-6"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="3000.00"
                  value={form.amount_dollars}
                  onChange={set('amount_dollars')}
                />
              </div>
            </div>
            <div>
              <label className="form-label">Frequency</label>
              <select className="form-select" value={form.frequency} onChange={set('frequency')}>
<option value="monthly">{t('personnel_detail.freq_monthly')}</option>
<option value="biweekly">Bi-weekly</option>
<option value="weekly">{t('personnel_detail.freq_weekly')}</option>
<option value="annual">{t('personnel_detail.freq_annual')}</option>
<option value="hourly">{t('personnel_detail.freq_hourly')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Effective from *</label>
              <input className="form-input" type="date" value={form.effective_from} onChange={set('effective_from')} />
            </div>
            <div>
              <label className="form-label">Effective to</label>
              <input className="form-input" type="date" value={form.effective_to} onChange={set('effective_to')} placeholder="Leave blank if current" />
            </div>
          </div>

          <div>
            <label className="form-label">Notes</label>
            <input className="form-input" value={form.notes} onChange={set('notes')} placeholder="e.g. Annual raise, new contract rate" />
          </div>

          {error && (
            <div className="text-sm text-flame-700 bg-flame-50 border border-flame-200 rounded-xl px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200">
          <button onClick={onClose} className="btn-ghost">{t('common.cancel')}</button>
          <button
            onClick={() => create.mutate()}
            disabled={!form.amount_dollars || !form.effective_from || create.isPending}
            className="btn-primary"
          >
            {create.isPending ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export function PersonnelDetailView() {
  const { t } = useTranslation()
  const { id }    = useParams<{ id: string }>()
  const personId  = Number(id)
  const navigate  = useNavigate()
  const { token } = useAuth()
  const qc        = useQueryClient()

  const [showAddSalary, setShowAddSalary] = useState(false)
  const [editField, setEditField]         = useState<string | null>(null)
  const [editValue, setEditValue]         = useState('')

  const { data: person, isLoading } = useQuery<Person>({
    queryKey: ['personnel-detail', personId],
    queryFn: () =>
      fetch(`/api/personnel/${personId}`, { headers: authHeaders(token) }).then(r => r.json()),
    enabled: !isNaN(personId),
  })

  const { data: salary = [] } = useQuery<SalaryEntry[]>({
    queryKey: ['personnel-salary', personId],
    queryFn: () =>
      fetch(`/api/personnel/${personId}/salary`, { headers: authHeaders(token) }).then(r => r.json()),
    enabled: !isNaN(personId),
  })

  const patch = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await fetch(`/api/personnel/${personId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
        body:    JSON.stringify(body),
      })
      if (!res.ok) { const d = await res.json(); throw new Error(d.error) }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['personnel-detail', personId] })
      qc.invalidateQueries({ queryKey: ['personnel'] })
      setEditField(null)
    },
  })

  if (isLoading || !person) {
    return (
      <div className="flex items-center justify-center py-20 text-ink-400">
        <Icon name="loader-circle" className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    )
  }

  const currentSalary = salary.find(s => !s.effective_to) ?? salary[0] ?? null

  function startEdit(field: string, value: string) {
    setEditField(field)
    setEditValue(value)
  }

  function EditableField({ field, label, value }: { field: string; label: string; value: string | null }) {
    if (editField === field) {
      return (
        <div>
          <label className="form-label">{label}</label>
          <div className="flex gap-2">
            <input
              className="form-input flex-1"
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              autoFocus
            />
            <button
              onClick={() => patch.mutate({ [field]: editValue || null })}
              disabled={patch.isPending}
              className="btn-primary text-xs px-3"
            >
              <Icon name="check" className="w-4 h-4" />
            </button>
            <button onClick={() => setEditField(null)} className="btn-ghost text-xs px-3">
              <Icon name="x" className="w-4 h-4" />
            </button>
          </div>
        </div>
      )
    }
    return (
      <div>
        <label className="form-label">{label}</label>
        <div
          onClick={() => startEdit(field, value ?? '')}
          className="text-[13px] text-ink-800 cursor-pointer hover:text-royal-600 flex items-center gap-1 group"
        >
          {value ?? <span className="text-ink-400">—</span>}
          <Icon name="key" className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Back */}
      <button onClick={() => navigate('/personnel')} className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800">
        <Icon name="chevron-left" className="w-4 h-4" /> Personnel
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">{person.full_name}</h1>
          <div className="flex items-center gap-2 mt-1">
            {person.title && <span className="text-sm text-ink-500">{person.title}</span>}
            <span className={STATUS_BADGE[person.status] ?? 'pill'}>{person.status.replace('_', ' ')}</span>
            <span className="text-sm text-ink-400 capitalize">{person.employment_type.replace('_', ' ')}</span>
          </div>
        </div>
        {currentSalary && (
          <div className="text-right">
            <div className="text-2xl font-bold text-ink-900">
              {fmtMoney(currentSalary.amount_usd_minor)}
            </div>
            <div className="text-sm text-ink-400">{FREQ_LABEL[currentSalary.frequency] ?? currentSalary.frequency}</div>
          </div>
        )}
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-2xl border border-ink-200 p-6">
        <h2 className="text-[13px] font-semibold text-ink-700 mb-4">Profile</h2>
        <div className="grid grid-cols-2 gap-4">
          <EditableField field="full_name" label="Full name" value={person.full_name} />
          <EditableField field="email"     label="Email"     value={person.email} />
          <EditableField field="title"     label="Title"     value={person.title} />
          <EditableField field="start_date" label="Start date" value={person.start_date} />
          <EditableField field="end_date"  label="End date"   value={person.end_date} />
          <div>
            <label className="form-label">Employment type</label>
            <select
              className="form-select"
              value={person.employment_type}
              onChange={e => patch.mutate({ employment_type: e.target.value })}
            >
<option value="employee">{t('personnel_detail.type_employee')}</option>
              <option value="contractor">{t('personnel_detail.type_contractor')}</option>
              <option value="part_time">Part-time</option>
            </select>
          </div>
        </div>
        <div className="mt-4">
          <EditableField field="notes" label="Notes" value={person.notes} />
        </div>
      </div>

      {/* Salary history */}
      <div className="bg-white rounded-2xl border border-ink-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
          <h2 className="text-[13px] font-semibold text-ink-700">Salary history</h2>
          <button onClick={() => setShowAddSalary(true)} className="btn-primary text-xs flex items-center gap-1.5">
            <Icon name="plus" className="w-3.5 h-3.5" /> Add entry
          </button>
        </div>

        {salary.length === 0 ? (
          <div className="py-10 text-center text-ink-400 text-sm">No salary entries yet</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] font-semibold text-ink-500 uppercase tracking-wide border-b border-ink-100">
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Frequency</th>
                <th className="px-4 py-3">Effective from</th>
                <th className="px-4 py-3">Effective to</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {salary.map(s => (
                <tr key={s.salary_id} className={s.effective_to ? 'opacity-60' : ''}>
                  <td className="px-4 py-3 font-medium text-ink-900">{fmtMoney(s.amount_usd_minor)}</td>
                  <td className="px-4 py-3 text-ink-500 capitalize">{s.frequency}</td>
                  <td className="px-4 py-3 mono text-ink-500">{s.effective_from}</td>
                  <td className="px-4 py-3 mono text-ink-500">
                    {s.effective_to ?? <span className="pill pill-mint text-[10px]">Current</span>}
                  </td>
                  <td className="px-4 py-3 text-ink-400">{s.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddSalary && (
        <AddSalaryModal personId={personId} onClose={() => setShowAddSalary(false)} />
      )}
    </div>
  )
}
